import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, renderSlot as _renderSlot } from "vue";
const _hoisted_1 = { class: "upload-documents" };
const _hoisted_2 = {
    key: 0,
    class: "text-error"
};
const _hoisted_3 = { class: "text-subtitle-2 font-weight-bold" };
const _hoisted_4 = { class: "text-body-2 text-medium-emphasis" };
const _hoisted_5 = {
    key: 6,
    class: "d-flex flex-wrap justify-end align-center ga-2 mt-4"
};
import { computed, onBeforeUnmount, ref } from "vue";
import DocumentDropZone from "@/components/DocumentDropZone.vue";
import PatientCasePicker from "@/components/PatientCasePicker.vue";
import { useDocumentUploads } from "@/composables/useDocumentUploads";
import { intakeService } from "@/services/intakeService";
import { pluralize, tryParseApiErrors } from "@/utils";
import { fullName } from "@/utils/person";
const CLOSE_DELAY_MS = 2000;
const MAX_STAGED_FILES = 20;
export default /*@__PURE__*/ _defineComponent({
    __name: 'UploadDocuments',
    props: {
        patient: {},
        caseId: {},
        newCaseOnly: { type: Boolean }
    },
    emits: ["uploaded", "done"],
    setup(__props, { emit: __emit }) {
        const DESTINATIONS = [
            { value: "new", title: "Start a new case", subtitle: "Document AI reads the files and creates a new case." },
            {
                value: "patient",
                title: "Add to patient record",
                subtitle: "Attach the files to a patient who is already in Lamar.",
            },
        ];
        const props = __props;
        const emit = __emit;
        // Mirrors MAX_UPLOADS in backend/portal/logic/intake/quick_start_evidence.py, the most links one batch may carry.
        const { documents, links, uploading, notice, add, remove, reset } = useDocumentUploads(MAX_STAGED_FILES);
        const destination = ref(props.patient ? "patient" : "new");
        const pickedChoice = ref(null);
        const phase = ref("editing");
        const submitError = ref(null);
        const successText = ref(null);
        let closeTimer = null;
        // Set when the caller passed a case, null included. Absent means the user picks the case.
        const fixedChoice = computed(() => props.patient && props.caseId !== undefined ? { patient: props.patient, caseId: props.caseId } : null);
        const targetChoice = computed(() => (destination.value === "patient" ? fixedChoice.value ?? pickedChoice.value : null));
        const busy = computed(() => phase.value !== "editing" || uploading.value);
        const canSubmit = computed(() => !busy.value && links.value.length > 0 && (destination.value === "new" || Boolean(targetChoice.value)));
        const submitLabel = computed(() => {
            if (destination.value === "new")
                return "Upload";
            const count = links.value.length;
            const files = count ? `${count} ${pluralize(count, "file")}` : "files";
            const target = targetChoice.value?.patient;
            return target ? `Add ${files} to ${fullName(target)}` : "Add to patient record";
        });
        const setDestination = (value) => {
            if (!value)
                return;
            destination.value = value;
            pickedChoice.value = null;
        };
        const onAddFiles = (files) => {
            submitError.value = null;
            void add(files);
        };
        const succeed = (text, payload) => {
            successText.value = text;
            phase.value = "done";
            emit("uploaded", payload);
            closeTimer = setTimeout(finish, CLOSE_DELAY_MS);
        };
        // Each file leaves the staged list once it is posted, so a retry after a partial failure posts only what failed.
        const postToIntake = async (target) => {
            const staged = documents.value.filter((doc) => doc.link);
            const results = await Promise.allSettled(staged.map(async (doc) => {
                await intakeService.postDemoUpload({ link: doc.link, patientId: target?.patient.id, caseId: target?.caseId });
                remove(doc);
            }));
            const failure = results.find((result) => result.status === "rejected");
            if (failure)
                throw failure.reason;
        };
        const submit = async () => {
            submitError.value = null;
            phase.value = "submitting";
            const count = links.value.length;
            const files = `${count} ${pluralize(count, "file")}`;
            try {
                const target = targetChoice.value;
                if (fixedChoice.value) {
                    const { patient, caseId } = fixedChoice.value;
                    const { taskIds } = await intakeService.addPatientDocuments(patient.id, {
                        links: links.value,
                        caseId,
                    });
                    succeed(`${files} added to ${fullName(patient)}`, { taskIds });
                    return;
                }
                await postToIntake(target);
                succeed(target ? `${files} added to ${fullName(target.patient)}` : `${files} uploaded`, null);
            }
            catch (error) {
                submitError.value = tryParseApiErrors(error)[0] ?? "The files could not be added. Try again.";
                phase.value = "editing";
            }
        };
        const finish = () => {
            closeTimer = null;
            reset();
            successText.value = null;
            phase.value = "editing";
            emit("done");
        };
        onBeforeUnmount(() => {
            if (closeTimer !== null)
                clearTimeout(closeTimer);
        });
        return (_ctx, _cache) => {
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_item = _resolveComponent("v-item");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_item_group = _resolveComponent("v-item-group");
            const _component_v_alert = _resolveComponent("v-alert");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(DocumentDropZone, {
                    disabled: phase.value !== 'editing',
                    staged: _unref(documents),
                    empty: "",
                    onAddFiles: onAddFiles
                }, null, 8, ["disabled", "staged"]),
                (_unref(documents).length)
                    ? (_openBlock(), _createBlock(_component_v_list, {
                        key: 0,
                        density: "compact",
                        class: "py-0 mt-2"
                    }, {
                        default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(documents), (doc, index) => {
                                return (_openBlock(), _createBlock(_component_v_list_item, {
                                    key: index,
                                    title: doc.file.name
                                }, {
                                    prepend: _withCtx(() => [
                                        (doc.uploading)
                                            ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                                                key: 0,
                                                indeterminate: "",
                                                size: "18",
                                                width: "2",
                                                color: "primary",
                                                class: "mr-3"
                                            }))
                                            : (_openBlock(), _createBlock(_component_v_icon, {
                                                key: 1,
                                                size: "18",
                                                class: "mr-3",
                                                color: doc.error ? 'error' : doc.link ? 'success' : 'grey',
                                                icon: doc.error ? 'mdi-alert-circle-outline' : doc.link ? 'mdi-check-circle' : 'mdi-file-outline'
                                            }, null, 8, ["color", "icon"]))
                                    ]),
                                    subtitle: _withCtx(() => [
                                        (doc.error)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(doc.error), 1))
                                            : _createCommentVNode("", true)
                                    ]),
                                    append: _withCtx(() => [
                                        _createVNode(_component_v_btn, {
                                            icon: "mdi-close",
                                            variant: "text",
                                            size: "small",
                                            "aria-label": `Remove ${doc.file.name}`,
                                            disabled: busy.value,
                                            onClick: ($event) => (_unref(remove)(doc))
                                        }, null, 8, ["aria-label", "disabled", "onClick"])
                                    ]),
                                    _: 2
                                }, 1032, ["title"]));
                            }), 128))
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (!__props.patient && !__props.newCaseOnly)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-body-2 font-weight-bold mt-4 mb-2" }, "Where should these go?", -1)),
                        _createVNode(_component_v_item_group, {
                            "model-value": destination.value,
                            mandatory: "",
                            "aria-label": "Where should these go?",
                            "onUpdate:modelValue": setDestination
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_row, null, {
                                    default: _withCtx(() => [
                                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(DESTINATIONS, (option) => {
                                            return _createVNode(_component_v_col, {
                                                key: option.value,
                                                cols: "12",
                                                sm: "6"
                                            }, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_item, {
                                                        value: option.value
                                                    }, {
                                                        default: _withCtx(({ isSelected, toggle }) => [
                                                            _createVNode(_component_v_card, {
                                                                flat: "",
                                                                class: _normalizeClass(["pa-3 destination-card", { 'destination-card--selected': isSelected }]),
                                                                role: "radio",
                                                                "aria-checked": isSelected,
                                                                onClick: toggle
                                                            }, {
                                                                default: _withCtx(() => [
                                                                    _createElementVNode("div", _hoisted_3, _toDisplayString(option.title), 1),
                                                                    _createElementVNode("div", _hoisted_4, _toDisplayString(option.subtitle), 1)
                                                                ]),
                                                                _: 2
                                                            }, 1032, ["class", "aria-checked", "onClick"])
                                                        ]),
                                                        _: 2
                                                    }, 1032, ["value"])
                                                ]),
                                                _: 2
                                            }, 1024);
                                        }), 64))
                                    ]),
                                    _: 1
                                })
                            ]),
                            _: 1
                        }, 8, ["model-value"])
                    ], 64))
                    : _createCommentVNode("", true),
                (destination.value === 'patient' && !fixedChoice.value)
                    ? (_openBlock(), _createBlock(PatientCasePicker, {
                        key: 2,
                        modelValue: pickedChoice.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((pickedChoice).value = $event)),
                        class: "mt-4",
                        patient: __props.patient
                    }, null, 8, ["modelValue", "patient"]))
                    : _createCommentVNode("", true),
                (_unref(notice))
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 3,
                        type: "warning",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-4",
                        text: _unref(notice)
                    }, null, 8, ["text"]))
                    : _createCommentVNode("", true),
                (submitError.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 4,
                        type: "error",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-4"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(submitError.value), 1)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (successText.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 5,
                        type: "success",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-4",
                        text: successText.value
                    }, null, 8, ["text"]))
                    : _createCommentVNode("", true),
                (phase.value !== 'done')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _renderSlot(_ctx.$slots, "actions"),
                        _createVNode(_component_v_btn, {
                            color: "primary",
                            variant: "flat",
                            disabled: !canSubmit.value,
                            loading: phase.value !== 'editing',
                            onClick: submit
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(submitLabel.value), 1)
                            ]),
                            _: 1
                        }, 8, ["disabled", "loading"])
                    ]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
